import { computed, effect, Injectable, signal } from '@angular/core'
import { BehaviorSubject, combineLatest, distinctUntilChanged, Observable } from 'rxjs'
import { BaseAsyncService } from './base-service'
import { BaseSignalStore } from './base-store'

/**
 * Global application state interface.
 * This should be extended based on your application's needs.
 */
export interface AppState {
  // User state
  user: User | null
  isAuthenticated: boolean

  // UI state
  isLoading: boolean
  sidebarOpen: boolean
  currentTheme: 'light' | 'dark'

  // Feature flags
  features: Record<string, boolean>

  // Navigation state
  currentRoute: string
  breadcrumbs: Breadcrumb[]
}

/**
 * User interface - extend based on your user model.
 */
export interface User {
  id: string
  name: string
  email: string
  roles: string[]
  avatar?: string
}

/**
 * Breadcrumb interface for navigation.
 */
export interface Breadcrumb {
  label: string
  route: string
  active?: boolean
}

/**
 * Global state manager that coordinates between different stores and services.
 * This service acts as the central hub for state management across the application.
 *
 * Features:
 * - Centralized state management
 * - Store coordination
 * - Cross-feature communication
 * - State persistence
 * - DevTools integration
 *
 * Usage:
 * ```typescript
 * constructor(private stateManager: StateManagerService) {}
 *
 * // Access state
 * const user = this.stateManager.user();
 * const isLoading = this.stateManager.isLoading();
 *
 * // Update state
 * this.stateManager.setUser(user);
 * this.stateManager.setLoading(true);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class StateManagerService {
  private readonly _state = signal<AppState>({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    sidebarOpen: true,
    currentTheme: 'light',
    features: {},
    currentRoute: '',
    breadcrumbs: [],
  })

  // Observable subjects for RxJS integration
  private readonly _stateSubject = new BehaviorSubject<AppState>(this._state())
  private readonly _userSubject = new BehaviorSubject<User | null>(null)
  private readonly _loadingSubject = new BehaviorSubject<boolean>(false)

  // Store registry for managing multiple stores
  private readonly stores = new Map<string, BaseSignalStore<unknown>>()
  private readonly services = new Map<string, BaseAsyncService>()

  constructor() {
    // Keep signal and subject in sync
    effect(() => {
      this._stateSubject.next(this._state())
    })

    // Initialize default state
    this.initializeState()
  }

  // Signal-based getters
  readonly state = this._state.asReadonly()
  readonly user = computed(() => this._state().user)
  readonly isAuthenticated = computed(() => this._state().isAuthenticated)
  readonly isLoading = computed(() => this._state().isLoading)
  readonly sidebarOpen = computed(() => this._state().sidebarOpen)
  readonly currentTheme = computed(() => this._state().currentTheme)
  readonly features = computed(() => this._state().features)
  readonly currentRoute = computed(() => this._state().currentRoute)
  readonly breadcrumbs = computed(() => this._state().breadcrumbs)

  // Observable-based getters
  readonly state$ = this._stateSubject.asObservable().pipe(distinctUntilChanged())
  readonly user$ = this._userSubject.asObservable().pipe(distinctUntilChanged())
  readonly loading$ = this._loadingSubject.asObservable().pipe(distinctUntilChanged())

  // Computed signals for derived state
  readonly userName = computed(() => this._state().user?.name ?? 'Guest')
  readonly userRoles = computed(() => this._state().user?.roles ?? [])
  readonly hasPermission = computed(
    () => (permission: string) => this._state().user?.roles.includes(permission) ?? false
  )

  /**
   * Updates the application state.
   * @param newState Partial state update
   */
  setState(newState: Partial<AppState>): void {
    this._state.update(current => ({ ...current, ...newState }))
  }

  /**
   * Sets the current user.
   * @param user User object or null
   */
  setUser(user: User | null): void {
    this.setState({ user, isAuthenticated: !!user })
    this._userSubject.next(user)
  }

  /**
   * Sets the loading state.
   * @param loading Loading state
   */
  setLoading(loading: boolean): void {
    this.setState({ isLoading: loading })
    this._loadingSubject.next(loading)
  }

  /**
   * Toggles the sidebar state.
   */
  toggleSidebar(): void {
    this.setState({ sidebarOpen: !this._state().sidebarOpen })
  }

  /**
   * Sets the current theme.
   * @param theme Theme to set
   */
  setTheme(theme: 'light' | 'dark'): void {
    this.setState({ currentTheme: theme })
    // Persist theme preference
    localStorage.setItem('theme', theme)
  }

  /**
   * Sets feature flags.
   * @param features Feature flags object
   */
  setFeatures(features: Record<string, boolean>): void {
    this.setState({ features })
  }

  /**
   * Sets the current route.
   * @param route Route path
   */
  setCurrentRoute(route: string): void {
    this.setState({ currentRoute: route })
  }

  /**
   * Sets breadcrumbs for navigation.
   * @param breadcrumbs Breadcrumb array
   */
  setBreadcrumbs(breadcrumbs: Breadcrumb[]): void {
    this.setState({ breadcrumbs })
  }

  /**
   * Registers a store with the state manager.
   * @param key Store key
   * @param store Store instance
   */
  registerStore<T>(key: string, store: BaseSignalStore<T>): void {
    this.stores.set(key, store as BaseSignalStore<unknown>)
  }

  /**
   * Gets a registered store.
   * @param key Store key
   * @returns Store instance or undefined
   */
  getStore<T>(key: string): BaseSignalStore<T> | undefined {
    return this.stores.get(key) as BaseSignalStore<T> | undefined
  }

  /**
   * Registers a service with the state manager.
   * @param key Service key
   * @param service Service instance
   */
  registerService(key: string, service: BaseAsyncService): void {
    this.services.set(key, service)
  }

  /**
   * Gets a registered service.
   * @param key Service key
   * @returns Service instance or undefined
   */
  getService<T extends BaseAsyncService>(key: string): T | undefined {
    return this.services.get(key) as T
  }

  /**
   * Creates a combined observable from multiple state sources.
   * @param sources State sources to combine
   * @returns Combined observable
   */
  combineState<T>(sources: Array<Observable<T>>): Observable<T[]> {
    return combineLatest(sources)
  }

  /**
   * Resets the entire application state.
   */
  reset(): void {
    this._state.set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      sidebarOpen: true,
      currentTheme: 'light',
      features: {},
      currentRoute: '',
      breadcrumbs: [],
    })

    // Reset all registered stores
    this.stores.forEach(store => store.reset())
  }

  /**
   * Persists the current state to localStorage.
   */
  persistState(): void {
    const state = this._state()
    const persistableState = {
      user: state.user,
      sidebarOpen: state.sidebarOpen,
      currentTheme: state.currentTheme,
      features: state.features,
    }

    localStorage.setItem('appState', JSON.stringify(persistableState))
  }

  /**
   * Restores state from localStorage.
   */
  restoreState(): void {
    try {
      const savedState = localStorage.getItem('appState')
      if (savedState) {
        const parsedState = JSON.parse(savedState)
        this.setState(parsedState)
      }
    } catch (error) {
      console.error('Failed to restore state:', error)
    }
  }

  /**
   * Initializes the application state.
   */
  private initializeState(): void {
    // Restore persisted state
    this.restoreState()

    // Set up theme from localStorage
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark'
    if (savedTheme) {
      this.setTheme(savedTheme)
    }

    // Set up feature flags from environment or API
    this.initializeFeatures()
  }

  /**
   * Initializes feature flags.
   */
  private initializeFeatures(): void {
    // This could be loaded from an API or environment configuration
    const defaultFeatures = {
      'dark-mode': true,
      notifications: true,
      analytics: false,
    }

    this.setFeatures(defaultFeatures)
  }
}
